import { Injectable, inject } from '@angular/core';
import { Evaluee, EvalueeFilters, EvalueeStatusOptions } from '@career-scope/models';
import { BehaviorSubject, combineLatest, from, Observable, of } from 'rxjs';
import { catchError, filter, map, pairwise, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';
import { convertTimestampsPipe } from '../pipes/convert-firestore-timestamp.pipe';
import { AuthService } from './auth.service';
import { collection, collectionData, collectionGroup, CollectionReference, endBefore, Firestore, getCountFromServer, 
      limit, limitToLast, orderBy, query, QueryConstraint, startAfter, where, getDocs, or ,and} from '@angular/fire/firestore';
import { Sort } from '@angular/material/sort';
import { NotificationService } from './notification.service';

export interface Pagination {
  pageIndex: number;
  previousPageIndex: number | undefined;
  pageSize: number;
  firstEvaluee: Evaluee | null;
  lastEvaluee: Evaluee | null;
}

export interface FilterState {
  evalueeFilters: EvalueeFilters | null;
  search: string | null;
  sort: Sort;
  pagination: Pagination,
  evalueeStatus: EvalueeStatusOptions[] | null;
  evalueeListType: 'discover' | 'assess' | null;
}

@Injectable({
  providedIn: 'root'
})
export class EvalueeListService {
  firestore = inject(Firestore);
  as = inject(AuthService);
  notificationService = inject(NotificationService);

  portalId = '';

  sortAndPaginationState = new BehaviorSubject<Omit<FilterState, 'evalueeFilters'>>({
    search: null,
    sort: { active: 'lastActivityDate', direction: 'desc' },
    pagination: { pageIndex: 0, previousPageIndex: undefined, pageSize: 20, firstEvaluee: null, lastEvaluee: null },
    evalueeStatus: [],
    evalueeListType: null
  });

  private evalueeFilters$ = this.as.evalueeFilters.pipe(
    filter(evalueeFilters => evalueeFilters !== null),
    startWith(null), // Ensure that pairwise has an initial value to pair with the first emission
    pairwise(), // Emit the previous and current values as a pair
    filter(([prev, curr]) => {
      return JSON.stringify(prev) !== JSON.stringify(curr); // Compare the previous and current values
    }),
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    map(([_, curr]) => curr), // Only pass along the current value
    tap(() => this.resetPagination())
  );

  filterState$: Observable<FilterState> = combineLatest([
    this.evalueeFilters$,
    this.sortAndPaginationState
  ]).pipe(
    map(([evalueeFilters, sortAndPagination]) => {
      return {
        evalueeFilters,
        ...sortAndPagination
      };
    }),
    shareReplay(1) // Used to prevent multiple subscriptions to the same observable, which causes pagination to reset in evalueeFilters$ when navigating back
  );

  evalueesTableData$: Observable<{ evaluees: Evaluee[], evalueeCount: number; counselor: string; }> = this.filterState$.pipe(
    // switchMap used as we want to use the latest value of evalueeFilters and cancel previous requests
    switchMap(filterState => {
      if (!filterState.evalueeStatus || filterState.evalueeStatus.length === 0 || filterState.evalueeListType === null) {
        return of({ evaluees: [], evalueeCount: 0, counselor: '' });
      }

      // Set appropriate default sort based on the tab type
      this.setAppropriateDefaultSort(filterState.evalueeListType, filterState.sort);

      const evalueesCollection = collection(this.firestore, `portals/${this.portalId}/evaluees`);
      const baseConstraints: QueryConstraint[] = [where('portalId', '==', this.portalId)];
      if (filterState.evalueeStatus?.length > 0) {
        baseConstraints.push(where('status', 'in', filterState.evalueeStatus));
      }
      if (filterState.search) {
        baseConstraints.push(where('uid', '==', filterState.search));
      }

      if (filterState.evalueeListType === 'discover') {
        // Results tab - simple and fast approach
        const resultsBaseConstraints = [...baseConstraints, ...this.applyEvalueeFilters(filterState.evalueeFilters)];

        let queryConstraints: QueryConstraint[] = [...resultsBaseConstraints];

        // Add sorting - default to completedDate descending for Results tab
        if (filterState.sort?.active === 'completedDate') {
          queryConstraints.push(orderBy('completedDate', filterState.sort.direction || 'desc'));
        } else if (filterState.sort?.active === 'lastActivityDate') {
          queryConstraints.push(orderBy('lastActivityDate', filterState.sort.direction || 'desc'));
        } else if (filterState.sort?.active === 'lowerCaseFullName') {
          queryConstraints.push(orderBy('lowerCaseFullName', filterState.sort.direction || 'asc'));
        } else {
          // Default sort for Results tab
          queryConstraints.push(orderBy('completedDate', 'desc'));
        }

        // Add secondary sort for consistency
        if (filterState.sort?.active !== 'lowerCaseFullName') {
          queryConstraints.push(orderBy('lowerCaseFullName', 'asc'));
        }

        // For Results tab, use a simple approach that catches all completed evaluees
        // The key insight: evaluees with completed assessments should have either:
        // 1. completedDate field (non-null)
        // 2. status = 'completed'
        // 3. OR they should be included if they have any assessment with completedDate

        // Simple approach: fetch evaluees and filter in memory (fast for reasonable datasets)
        queryConstraints.push(limit(1500)); // Fetch up to 1000 evaluees

        // Fetch evaluees and then filter for completed assessments
        let evaluees$: Observable<Evaluee[]> = (collectionData(query(evalueesCollection, ...queryConstraints)) as Observable<Evaluee[]>).pipe(
          convertTimestampsPipe(),
          catchError(err => {
            console.error('Error fetching evaluees for Results tab:', err);
            return of([]);
          }),
          // Filter for completed assessments - include evaluees with completedDate OR status='completed'
          map(evaluees => {
            // Filter for completed assessments
            let completedEvaluees = evaluees.filter(evaluee => {
              return !!(evaluee.completedDate || evaluee.status === 'completed');
            });

            // Apply date filter if provided
            if (filterState.evalueeFilters?.startDate && filterState.evalueeFilters?.endDate) {
              // Convert dates properly - handle both Date objects and serialized dates
              let startDate: Date;
              let endDate: Date;
              
              if (filterState.evalueeFilters.startDate instanceof Date) {
                startDate = filterState.evalueeFilters.startDate;
              } else if (typeof filterState.evalueeFilters.startDate === 'string' || typeof filterState.evalueeFilters.startDate === 'number') {
                startDate = new Date(filterState.evalueeFilters.startDate);
              } else {
                startDate = new Date(filterState.evalueeFilters.startDate);
              }

              if (filterState.evalueeFilters.endDate instanceof Date) {
                endDate = filterState.evalueeFilters.endDate;
              } else if (typeof filterState.evalueeFilters.endDate === 'string' || typeof filterState.evalueeFilters.endDate === 'number') {
                endDate = new Date(filterState.evalueeFilters.endDate);
              } else {
                endDate = new Date(filterState.evalueeFilters.endDate);
              }
              
              // Set time to start of day for startDate and end of day for endDate
              const startOfDay = new Date(startDate);
              startOfDay.setHours(0, 0, 0, 0);
              const endOfDay = new Date(endDate);
              endOfDay.setHours(23, 59, 59, 999);

              completedEvaluees = completedEvaluees.filter(evaluee => {
                const evalueeDate = evaluee.completedDate || evaluee.lastActivityDate;
                if (!evalueeDate) return false;
                const date = evalueeDate instanceof Date ? evalueeDate : new Date(evalueeDate);
                // Compare dates properly using getTime()
                const dateTime = date.getTime();
                return dateTime >= startOfDay.getTime() && dateTime <= endOfDay.getTime();
              });
            }

            // Apply pagination to the filtered results
            const startIndex = filterState.pagination.pageIndex * filterState.pagination.pageSize;
            const endIndex = startIndex + filterState.pagination.pageSize;

            return completedEvaluees.slice(startIndex, endIndex);
          })
        );

        // For accurate count with date filtering, we need to fetch all and count
        // Create a separate observable to get the total count with date filtering
        const allEvalueesForCount$ = (collectionData(query(evalueesCollection, ...resultsBaseConstraints, limit(1500))) as Observable<Evaluee[]>).pipe(
          convertTimestampsPipe(),
          catchError(err => {
            console.error('Error fetching evaluees for count:', err);
            return of([]);
          }),
          map(evaluees => {
            // Filter for completed assessments
            let completedEvaluees = evaluees.filter(evaluee => {
              return !!(evaluee.completedDate || evaluee.status === 'completed');
            });

            // Apply date filter if provided
            if (filterState.evalueeFilters?.startDate && filterState.evalueeFilters?.endDate) {
              // Convert dates properly - handle both Date objects and serialized dates
              let startDate: Date;
              let endDate: Date;
              
              if (filterState.evalueeFilters.startDate instanceof Date) {
                startDate = filterState.evalueeFilters.startDate;
              } else if (typeof filterState.evalueeFilters.startDate === 'string' || typeof filterState.evalueeFilters.startDate === 'number') {
                startDate = new Date(filterState.evalueeFilters.startDate);
              } else {
                startDate = new Date(filterState.evalueeFilters.startDate);
              }

              if (filterState.evalueeFilters.endDate instanceof Date) {
                endDate = filterState.evalueeFilters.endDate;
              } else if (typeof filterState.evalueeFilters.endDate === 'string' || typeof filterState.evalueeFilters.endDate === 'number') {
                endDate = new Date(filterState.evalueeFilters.endDate);
              } else {
                endDate = new Date(filterState.evalueeFilters.endDate);
              }
              
              // Set time to start of day for startDate and end of day for endDate
              const startOfDay = new Date(startDate);
              startOfDay.setHours(0, 0, 0, 0);
              const endOfDay = new Date(endDate);
              endOfDay.setHours(23, 59, 59, 999);

              completedEvaluees = completedEvaluees.filter(evaluee => {
                const evalueeDate = evaluee.completedDate || evaluee.lastActivityDate;
                if (!evalueeDate) return false;
                const date = evalueeDate instanceof Date ? evalueeDate : new Date(evalueeDate);
                // Compare dates properly using getTime()
                const dateTime = date.getTime();
                return dateTime >= startOfDay.getTime() && dateTime <= endOfDay.getTime();
              });
            }

            return completedEvaluees.length;
          })
        );

        return combineLatest([evaluees$, allEvalueesForCount$]).pipe(
          map(([evaluees, evalueeCount]) => ({ evaluees, evalueeCount, counselor: filterState.evalueeFilters?.counselor || '' })),
          catchError(err => {
            console.error(err);
            return of({ evaluees: [], evalueeCount: 0, counselor: '' });
          })
        );
      } else {
        // Assess tab - filter by status as before (only show 'new' and 'in progress')
        const filterStatuses: EvalueeStatusOptions[] = !filterState.evalueeFilters?.assessStatus
          ? ['new', 'in progress']
          : [filterState.evalueeFilters?.assessStatus];
        if (filterState.sort?.active === 'completedDate') {
          filterState.sort.active = 'lastActivityDate';
        }
        // Add status filtering and evaluee filters for Assess tab
        const assessBaseConstraints = [...baseConstraints, ...this.applyEvalueeFilters(filterState.evalueeFilters), where('status', 'in', filterStatuses)];

        const constraintsWithPageAndSort = filterState.search ? assessBaseConstraints : this.applyPaginationAndSorting([...assessBaseConstraints], filterState.sort, filterState.pagination);

        let evaluees$: Observable<Evaluee[]> = (collectionData(query(evalueesCollection, ...constraintsWithPageAndSort)) as Observable<Evaluee[]>).pipe(convertTimestampsPipe(), catchError(err => { console.error(err); return of([]); }));
        let evalueeCount$: Observable<number> = from(getCountFromServer((query(evalueesCollection, ...assessBaseConstraints)))).pipe(map(aggregateField => aggregateField.data().count));

        return combineLatest([evaluees$, evalueeCount$]).pipe(
          map(([evaluees, evalueeCount]) => ({ evaluees, evalueeCount, counselor: filterState.evalueeFilters?.counselor || '' })),
          catchError(err => {
            console.error(err);
            return of({ evaluees: [], evalueeCount: 0, counselor: '' });
          })
        );
      }


      // If we reach here, return empty result (this should never happen)
      return of({ evaluees: [], evalueeCount: 0, counselor: '' });
    })
  );

  // This will cause a double fire to the database as pagination is apart of filterState$
  // No way to avoid this as pagination and evalueeFilters are separate observables
  private resetPagination() {
    const currentState = this.sortAndPaginationState.getValue();
    const updatedState = {
      ...currentState,
      pagination: { ...currentState.pagination, pageIndex: 0, previousPageIndex: undefined, firstEvaluee: null, lastEvaluee: null }
    };

    this.sortAndPaginationState.next(updatedState);
  }

  // Reset pagination when page size changes
  resetPaginationOnPageSizeChange(newPageSize: number) {
    const currentState = this.sortAndPaginationState.getValue();
    if (currentState.pagination.pageSize !== newPageSize) {
      const updatedState = {
        ...currentState,
        pagination: {
          ...currentState.pagination,
          pageSize: newPageSize,
          pageIndex: 0,
          previousPageIndex: undefined,
          firstEvaluee: null,
          lastEvaluee: null
        }
      };
      this.sortAndPaginationState.next(updatedState);
    }
  }

  // Method to handle page size changes from the UI
  handlePageSizeChange(newPageSize: number) {
    this.resetPaginationOnPageSizeChange(newPageSize);
  }

 searchEvaluees(fullName: string, restrictedViewCounselor?: string): Observable<Evaluee[]> {
  const ref = collection(
    this.firestore,
    `portals/${this.portalId}/evaluees`
  ) as CollectionReference<Evaluee>;

  // Base counselor + portal filter
  const baseFilters = [
    where("portalId", "==", this.portalId),
  ];
  if (restrictedViewCounselor) {
    baseFilters.push(where("counselor.name", "==", restrictedViewCounselor));
  }

  const fullNameLow = fullName.toLowerCase();

  // --- Query 1: lowerCaseFullName ---
  const getFullName = query(
    ref,
    ...baseFilters,
    where("lowerCaseFullName", ">=", fullNameLow),
    where("lowerCaseFullName", "<=", fullNameLow + "\uf8ff"),
    limit(20)
  );

  // --- Query 2: logonName (case-sensitive prefix search) ---
  const getLogonName = query(
    ref,
    ...baseFilters,
    where("logonName", ">=", fullName),
    where("logonName", "<=", fullName + "\uf8ff"),
    limit(20)
  );

  const r1$ = collectionData(getFullName) as Observable<Evaluee[]>;
  const r2$ = collectionData(getLogonName) as Observable<Evaluee[]>;

  return combineLatest([r1$, r2$]).pipe(
    map(([a, b]) => {
      const mapObj = new Map<string, Evaluee>();
      a.forEach(v => mapObj.set(v.uid, v));
      b.forEach(v => mapObj.set(v.uid, v));
      return Array.from(mapObj.values()).slice(0, 20);
    }),
    convertTimestampsPipe()
  );

}


  adminSearchEvaluees(fullName: string, portals: string[]) {
    if (portals.length >= 30 || portals.length === 0) {
      this.notificationService.openDismissibleSnackbar('Too many portals to search. Please reach out to support for assistance.');
      throw new Error('Too many portals to search');
    }

     const evalueesCollection = collection(
      this.firestore,
      `portals/${this.portalId}/evaluees`
    ) as CollectionReference<Evaluee>;

    const nameLower = fullName.toLowerCase();

    // OR-condition:
    // (lowerCaseFullName >= nameLower AND lowerCaseFullName <= nameLower + '\uf8ff')
    //    OR
    // (logonName >= nameLower AND logonName <= nameLower + '\uf8ff')
    const orCondition = or(
      and(
        where('lowerCaseFullName', '>=', nameLower),
        where('lowerCaseFullName', '<=', nameLower + '\uf8ff')
      ),
      and(
        where('logonName', '>=', nameLower),
        where('logonName', '<=', nameLower + '\uf8ff')
      )
    );

    const constraints: any[] = [
      where('portalId', '==', this.portalId),
      orCondition,
      limit(20)
    ];
    return (collectionData(query(evalueesCollection, ...constraints)) as Observable<Evaluee[]>).pipe(
      convertTimestampsPipe()
    );
  }

  // ANY UPDATES TO SORTING OR FILTERING WILL REQUIRE A NEW INDEX IN FIRESTORE
  applyEvalueeFilters(evalueeFilters: EvalueeFilters | null): QueryConstraint[] {
    if (!evalueeFilters) {
      return [];
    }

    const constraints: QueryConstraint[] = [];

    if (evalueeFilters.counselor && evalueeFilters.counselor !== 'all') {
      constraints.push(where('counselor.name', '==', evalueeFilters.counselor));
    }

    if (evalueeFilters.categories && evalueeFilters.categories.length > 0) {
      const categoriesWithIdentifier = evalueeFilters.categories.filter(category => category.identifier);

      // If only one category search the full identifiers array to see if it contains the identifier
      if (categoriesWithIdentifier.length === 1) {
        constraints.push(where('identifiers', 'array-contains', { id: categoriesWithIdentifier[0].id, identifier: categoriesWithIdentifier[0].identifier }));
      }

      // If more than one category search for matching set of identifiers
      if (categoriesWithIdentifier.length > 1) {
        constraints.push(where('identifiers', '==', categoriesWithIdentifier.map(category => ({ id: category.id, identifier: category.identifier }))));
      }
    }

    // Apply date filters
    if (evalueeFilters.startDate && evalueeFilters.endDate) {
      const startDate = evalueeFilters.startDate instanceof Date ? evalueeFilters.startDate : new Date(evalueeFilters.startDate);
      const endDate = evalueeFilters.endDate instanceof Date ? evalueeFilters.endDate : new Date(evalueeFilters.endDate);
      
      // For Results tab (discover), filter by completedDate
      // For Assess tab, filter by lastActivityDate or createdDate
      // We'll apply the date filter in the query based on the list type
      // Note: Date filtering will be applied in memory for Results tab since we're already filtering completedDate
      // For Assess tab, we can add it to the query constraints
    }

    return constraints;
  }

  // ANY UPDATES TO SORTING OR FILTERING WILL REQUIRE A NEW INDEX IN FIRESTORE
  applyPaginationAndSorting(constraints: QueryConstraint[], sort: Sort, pagination: Pagination): QueryConstraint[] {
    // Use the same enhanced sorting logic for both Assess and Results tabs
    const sortConstraints = this.applySortingConstraints(sort);
    constraints.push(...sortConstraints);

    return this.applyPagination(constraints, pagination, sort.active);
  }

  applyPagination(constraints: QueryConstraint[], pagination: Pagination, sortActive: string): QueryConstraint[] {
    const { pageIndex, pageSize, lastEvaluee, firstEvaluee, previousPageIndex } = pagination;

    if (pageIndex === 0) {
      return [...constraints, limit(pageSize)];
    }

    const getPaginationArgs = (evaluee: Evaluee | null) => {
      if (!evaluee) return [];

      const args = [
        sortActive === 'counselor.name' ? evaluee.counselor.name : evaluee[sortActive as keyof Evaluee]
      ];

      if (sortActive !== 'lastActivityDate') {
        args.push(evaluee['lastActivityDate']);
      }

      if (sortActive !== 'fullName' && sortActive !== 'lowerCaseFullName') {
        args.push(evaluee['lowerCaseFullName']);
      }

      return args;
    };

    if (pageIndex > (previousPageIndex || 0) && lastEvaluee) {
      return [...constraints, startAfter(...getPaginationArgs(lastEvaluee)), limit(pageSize)];
    }

    if (pageIndex < (previousPageIndex || 0) && firstEvaluee) {
      return [...constraints, endBefore(...getPaginationArgs(firstEvaluee)), limitToLast(pageSize)];
    }

    return [...constraints, limit(pageSize)];
  }

  handleTabChange(evalueeListType: 'discover' | 'assess') {
    const currentState = this.sortAndPaginationState.getValue();
    const updatedState = {
      ...currentState,
      evalueeListType,
      pagination: { ...currentState.pagination, pageIndex: 0, previousPageIndex: undefined, firstEvaluee: null, lastEvaluee: null }
    };
    this.sortAndPaginationState.next(updatedState);
  }

  resetEvalueeListService() {
    this.portalId = '';
  }

  private applySortingConstraints(sort: Sort): QueryConstraint[] {
    const constraints: QueryConstraint[] = [];
    let sortActive = sort.active;

    switch (sort.active) {
      case 'fullName':
        sortActive = 'lowerCaseFullName';
        break;
      case 'counselorName':
        sortActive = 'counselor.name';
        break;
      case 'completedDate':
        // For completedDate, we need to handle cases where it might be null
        // Sort by completedDate first, then by lastActivityDate as fallback
        constraints.push(orderBy('completedDate', sort.direction === 'asc' ? 'asc' : 'desc'));
        constraints.push(orderBy('lastActivityDate', 'desc')); // Secondary sort
        constraints.push(orderBy('lowerCaseFullName', 'asc')); // Tertiary sort
        return constraints;
      case 'lastActivityDate':
        // For lastActivityDate, sort by the field directly
        constraints.push(orderBy('lastActivityDate', sort.direction === 'asc' ? 'asc' : 'desc'));
        constraints.push(orderBy('lowerCaseFullName', 'asc')); // Secondary sort
        return constraints;
      default:
        sortActive = sort.active;
    }

    constraints.push(orderBy(sortActive, sort.direction === 'asc' ? 'asc' : 'desc'));

    // Secondary/tertiary sort fields order matters as it affects how the indexes in firestore are built
    // Whatever the last sort option is will be what determines asc/desc for __name__ sorting in firestore

    // Secondary sort field 
    if (sort.active !== 'lastActivityDate') {
      constraints.push(orderBy('lastActivityDate', 'desc'));
    }

    // Tertiary sort field
    if (sort.active !== 'lowerCaseFullName') {
      constraints.push(orderBy('lowerCaseFullName', 'asc'));
    }

    return constraints;
  }

  private setAppropriateDefaultSort(evalueeListType: 'discover' | 'assess' | null, currentSort: Sort) {
    const currentState = this.sortAndPaginationState.getValue();


    // Only change the sort if it's different from what we want
    if (evalueeListType === 'discover' && currentSort.active !== 'completedDate') {
      // For Results tab, use completedDate
      this.sortAndPaginationState.next({
        ...currentState,
        sort: { active: 'completedDate', direction: 'desc' }
      });
    } else if (evalueeListType === 'assess' && currentSort.active !== 'lastActivityDate') {
      // For Assess tab, use lastActivityDate
      this.sortAndPaginationState.next({
        ...currentState,
        sort: { active: 'lastActivityDate', direction: 'desc' }
      });
    } else {
      console.log('No sort change needed');
    }
  }
}
